import { renderRoutes } from 'react-router-config'
import React, { useEffect, useMemo, useCallback } from 'react'
import { useQuery } from 'react-query'
import { useTranslation } from 'react-i18next'
import { FacebookProvider } from 'react-facebook'
import emmit from '@/pages/lna/utils/emitter'
import { get } from '@/pages/lna/utils/fetch'
import { authToken } from '@/utils/helper'
import useTA from '@/pages/lna/hooks/useTA'
import { isPC } from '@/utils'
import {
  useStoreReducer,
  StoreContext,
  MusicContext,
  storeActionType,
  musicActionType,
  useMusicReducer,
} from '@/pages/lna/context'
import { TOKEN_KEY } from '@/utils/cookie'

const whiteList = ['/reservation/sakura2024', '/odin', '/discord/login-bind']

/**
 * BaseLayout
 */
const BaseLayout = (props: React.PropsWithChildren<any>) => {
  const { route, children } = props
  const [store, dispatch] = useStoreReducer()
  const { t } = useTranslation()
  const [musicStore, musicDispatch] = useMusicReducer()
  const { initTa } = useTA()

  useEffect(() => {
    initTa?.()
  }, [initTa])

  const stopMusic = useCallback(() => {
    musicDispatch({
      type: musicActionType.MUSIC_STATUS,
      data: false,
    })
  }, [musicDispatch])

  const storeCtxValue = useMemo(() => ({ store, dispatch }), [dispatch, store])
  const musicCtxValue = useMemo(
    () => ({ ...musicStore, stopMusic, musicDispatch }),
    [musicDispatch, musicStore, stopMusic]
  )

  const getReserveStatus = () => {
    if (authToken() && !store.reserveStatus) {
      get('/api/appointment').then((res) => {
        dispatch({
          type: storeActionType.RESERVE_STATUS,
          data: res?.data?.is_appointment,
        })
      })
    }
  }

  const getPreRegistrationCount = () => {
    get('/api/appointment-info', {
      activity_code: 'appointment_zone',
    }).then((res) => {
      dispatch({
        type: storeActionType.RESERVE_COUNT,
        data: res?.data?.count,
      })
    })
  }

  useEffect(() => {
    getReserveStatus()
    getPreRegistrationCount()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store.loginStatus])

  const { refetch: getJobIntro } = useQuery<
    unknown,
    ReturnData | Error,
    ReturnData
  >('/lna/api/job-intro', async () => get('/api/job-intro'), {
    refetchOnWindowFocus: false,
    retry: false,
    onSuccess: (res) => {
      dispatch({
        type: storeActionType.UPDATE_HOMEDATA,
        data: res?.data,
      })
    },
  })
  // 获取包体下载链接低地址
  useQuery<unknown, ReturnData | Error, ReturnData>(
    '/api/reservation/download',
    async () => get('/api/reservation/download'),
    {
      refetchOnWindowFocus: false,
      retry: false,
      onSuccess: (res) => {
        dispatch({
          type: storeActionType.UPDATE_DOWNLOAD_INFO,
          data: res?.data,
        })
      },
    }
  )

  const getData = (res: any) => [
    {
      id: 0,
      name: t('lna.recent'),
      weight: 0,
    },
    ...((res?.data?.news_categories as any[]) ?? []),
  ]

  const { refetch: getNewsCategories } = useQuery<
    unknown,
    ReturnData | Error,
    ReturnData
  >('/lna/api/news-categories', async () => get('/api/news-categories'), {
    refetchOnWindowFocus: false,
    retry: false,
    onSuccess: (res) => {
      dispatch({
        type: storeActionType.UPDATE_NEWS_CATEGORIES,
        data: getData(res),
      })
    },
  })

  useEffect(() => {
    emmit.on('getJobIntro', () => {
      getJobIntro()
    })
    return () => {
      emmit.off('getJobIntro', () => {
        getJobIntro()
      })
    }
  }, [getJobIntro])

  useEffect(() => {
    const { pathname, search } = window.location
    //  处理预注册页面路径
    if (pathname?.includes('reserve')) return
    if (whiteList.includes(pathname)) return
    const path = pathname.slice(1).split('/') || []
    if (isPC()) {
      if (path[0] === 'm') {
        window.location.replace(`/${path.slice(1).join('/')}${search}`)
      }
    } else if (path[0] !== 'm') {
      window.location.replace(`/m${pathname}${search}`)
    }
  }, [])

  useEffect(() => {
    if (authToken()) {
      dispatch({
        type: storeActionType.LOGIN_STATUS,
        data: true,
      })
      dispatch({
        type: storeActionType.USER_INFO,
        data: JSON.parse(localStorage.getItem(TOKEN_KEY) || '{}')?.account_info,
      })
    }
  }, [dispatch])

  const lang = localStorage.getItem('i18nextLng') || 'en'
  return (
    <div className="layout-container">
      <FacebookProvider appId="820791479705055" language={lang}>
        <StoreContext.Provider value={storeCtxValue}>
          <MusicContext.Provider value={musicCtxValue}>
            {renderRoutes(route.routes)}
            {children}
          </MusicContext.Provider>
        </StoreContext.Provider>
      </FacebookProvider>
    </div>
  )
}

export default React.memo(BaseLayout)
